import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, Link, Typography } from '@mui/material';
import { useListContext, useGetList, useGetMany } from 'react-admin';

import productTitleFromSlug from '@src/utils/productTitleFromSlug';

const STABLE_EMPTY_ARRAY = [];

const PRODUCT_FILTER_KEYS = new Set([
    'product_ids',
    'product_id',
    'products',
    'product_slugs',
]);

const PLATFORMS_PARAMS = {
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'name', order: 'ASC' },
};

const humanize = (str) => {
    if (!str) return '';
    return str
        .replace(/_ids?$/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const findMatchingChild = (children, sourceKey) => {
    let result = null;
    React.Children.forEach(children, (child) => {
        if (result || !React.isValidElement(child)) return;
        if (child.props.source === sourceKey) {
            result = child;
        } else if (child.props.children) {
            result = findMatchingChild(child.props.children, sourceKey);
        }
    });
    return result;
};

const findChoicesInInput = (inputChild) => {
    if (!inputChild) return null;
    if (inputChild.props?.choices && Array.isArray(inputChild.props.choices)) {
        return inputChild.props.choices;
    }

    if (inputChild.props?.children) {
        let nestedChoices = null;
        React.Children.forEach(inputChild.props.children, (c) => {
            if (nestedChoices) return;
            if (React.isValidElement(c)) {
                nestedChoices = findChoicesInInput(c);
            }
        });

        return nestedChoices;
    }

    return null;
};

const findReferenceInInput = (inputChild, filterKey) => {
    if (inputChild?.props?.reference) {
        return inputChild.props.reference;
    }

    if (inputChild?.props?.children) {
        let nestedRef = null;
        React.Children.forEach(inputChild.props.children, (c) => {
            if (nestedRef) return;
            if (React.isValidElement(c)) {
                nestedRef = findReferenceInInput(c, filterKey);
            }
        });
        if (nestedRef) return nestedRef;
    }

    if (
        filterKey === 'product_ids' ||
        filterKey === 'product_id' ||
        filterKey === 'products' ||
        filterKey === 'product_slugs'
    ) {
        return 'products';
    }

    return null;
};

const extractIds = (val) => {
    if (val === undefined || val === null || val === '') return [];
    const arr = Array.isArray(val) ? val : [val];
    return arr
        .map((v) => (v && typeof v === 'object' ? v.id || v.slug || v.value : v))
        .filter((v) => v !== undefined && v !== null && v !== '');
};

const recordsFromData = (data) => {
    if (!data) return STABLE_EMPTY_ARRAY;
    if (Array.isArray(data)) return data.filter(Boolean);
    if (typeof data === 'object') {
        return Object.values(data).filter((item) => item && typeof item === 'object' && !Array.isArray(item));
    }

    return STABLE_EMPTY_ARRAY;
};

const productsFromPlatforms = (platforms) => (
    (platforms || []).flatMap((platform) => platform.products || [])
);

const recordLabel = (record) => record?.name || record?.label || record?.title || record?.id;

const matchesRecord = (record, itemKey) => (
    String(record?.id) === String(itemKey) ||
    String(record?.value) === String(itemKey) ||
    String(record?.slug) === String(itemKey)
);

const formatChoiceValue = (val, choices, referenceRecords, filterKey) => {
    const itemKey = val && typeof val === 'object' ? val.id || val.slug || val.value : val;
    if (itemKey === undefined || itemKey === null || itemKey === '') return null;

    const match = [...(choices || []), ...referenceRecords].find((record) => matchesRecord(record, itemKey));
    if (match) return recordLabel(match);

    if (PRODUCT_FILTER_KEYS.has(filterKey)) {
        return productTitleFromSlug(String(itemKey));
    }

    return null;
};

const formatFilterValue = (val, inputChild, referenceRecords, filterKey) => {
    const choices = findChoicesInInput(inputChild);

    if (Array.isArray(val)) {
        if (val.length === 0) return null;

        const mapped = val.map((v) => {
            const choiceName = formatChoiceValue(v, choices, referenceRecords, filterKey);
            if (choiceName) return choiceName;

            if (typeof v === 'boolean') return v ? 'Yes' : 'No';

            if (v && typeof v === 'object') {
                try {
                    return JSON.stringify(v);
                } catch {
                    return String(v);
                }
            }

            return String(v);
        });

        return mapped.join(', ');
    }

    if (typeof val === 'boolean') {
        return val ? 'Yes' : 'No';
    }

    const choiceName = formatChoiceValue(val, choices, referenceRecords, filterKey);
    if (choiceName) return choiceName;

    if (val && typeof val === 'object') {
        try {
            return JSON.stringify(val);
        } catch {
            return String(val);
        }
    }

    return String(val);
};

const AppliedFilterChip = ({ filterKey, val, inputChild, onRemove }) => {
    const rawLabel = inputChild?.props?.label;
    const label = rawLabel || humanize(filterKey);

    const reference = findReferenceInInput(inputChild, filterKey);
    const ids = useMemo(() => extractIds(val), [val]);
    const isProductFilter = PRODUCT_FILTER_KEYS.has(filterKey);

    const { data: referenceData } = useGetMany(
        reference,
        { ids },
        { enabled: Boolean(reference && ids.length > 0) }
    );
    const { data: platforms = STABLE_EMPTY_ARRAY } = useGetList(
        'platforms',
        PLATFORMS_PARAMS,
        { enabled: isProductFilter },
    );

    const referenceRecords = [
        ...recordsFromData(referenceData),
        ...(isProductFilter ? productsFromPlatforms(platforms) : STABLE_EMPTY_ARRAY),
    ];

    const formattedVal = formatFilterValue(val, inputChild, referenceRecords, filterKey);

    return (
        <Chip
            label={
                <span>
                    <strong>{label}:</strong> {formattedVal}
                </span>
            }
            size="small"
            sx={{
                backgroundColor: 'action.hover',
                borderColor: 'divider',
                borderRadius: '16px',
                fontWeight: 400,
                '& .MuiChip-deleteIcon': {
                    fontSize: '1rem',
                },
            }}
            variant="outlined"
            onDelete={onRemove}
        />
    );
};

AppliedFilterChip.propTypes = {
    filterKey: PropTypes.string.isRequired,
    inputChild: PropTypes.node,
    onRemove: PropTypes.func.isRequired,
    val: PropTypes.any,
};

AppliedFilterChip.defaultProps = {
    inputChild: null,
    val: null,
};

const AppliedFilters = ({ filterInputs }) => {
    const { filterValues, displayedFilters, setFilters } = useListContext();

    if (!filterValues || typeof filterValues !== 'object') {
        return null;
    }

    const activeKeys = Object.keys(filterValues).filter((key) => {
        const val = filterValues[key];

        if (val === undefined || val === null || val === '') return false;
        if (Array.isArray(val) && val.length === 0) return false;

        return true;
    });

    if (activeKeys.length === 0) {
        return null;
    }

    const handleRemoveFilter = (keyToRemove, e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        const nextFilters = { ...filterValues };
        delete nextFilters[keyToRemove];

        const nextDisplayedFilters = { ...displayedFilters };
        delete nextDisplayedFilters[keyToRemove];

        setFilters(nextFilters, nextDisplayedFilters);
    };

    const handleClearAll = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        setFilters({}, {});
    };

    return (
        <Box
            className="applied-filters-container"
            sx={{
                alignItems: 'center',
                display: 'flex',
                flex: '1 0 100%',
                flexBasis: '100%',
                flexWrap: 'wrap',
                gap: 1,
                mb: 1,
                mt: 1.5,
                px: 0.5,
                width: '100%',
            }}
        >
            <Typography
                sx={{
                    color: 'text.secondary',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    mr: 0.5,
                }}
                variant="body2"
            >
                Applied:
            </Typography>

            {activeKeys.map((key) => {
                const val = filterValues[key];
                const inputChild = findMatchingChild(filterInputs, key);

                return (
                    <AppliedFilterChip
                        key={key}
                        filterKey={key}
                        inputChild={inputChild}
                        val={val}
                        onRemove={(e) => handleRemoveFilter(key, e)}
                    />
                );
            })}

            <Link
                component="button"
                sx={{
                    background: 'none',
                    border: 'none',
                    color: 'primary.main',
                    cursor: 'pointer',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    ml: 1,
                    padding: 0,
                }}
                type="button"
                underline="hover"
                variant="body2"
                onClick={handleClearAll}
            >
                Clear all
            </Link>
        </Box>
    );
};

AppliedFilters.propTypes = {
    filterInputs: PropTypes.node,
};

AppliedFilters.defaultProps = {
    filterInputs: null,
};

export default AppliedFilters;
